"use client";

import { Typography } from "./typography.js";
import { cn, toCurrency, clamp, toPercentString } from "@drpcrd/utils";
import { calculateRateCardMetrics } from "@drpcrd/utils";
import { ArrowDown } from "lucide-react";

type SegmentUnits = {
  red: number;
  orange: number;
  green: number;
};

export type RateCardComparisonZone =
  | "green"
  | "orangeLow"
  | "orangeHigh"
  | "redLow"
  | "redHigh";

type Props = {
  referenceValue: number;
  value: number;
  currency: "GBP" | "USD" | "EUR";
  totalUnits?: number;
  unitValue?: number;
  segmentUnits?: SegmentUnits;
  className?: string;
  variant: "creator" | "brand";
};

const ARROW_EDGE_OVERHANG_PX = 12;
const DEFAULT_TOTAL_UNITS = 100;
const DEFAULT_SEGMENT_UNITS: SegmentUnits = { red: 9, orange: 18, green: 46 };

export const getRateCardComparisonZone = ({
  referenceValue,
  value,
  totalUnits = DEFAULT_TOTAL_UNITS,
  unitValue,
  segmentUnits = DEFAULT_SEGMENT_UNITS,
}: {
  referenceValue: number;
  value: number;
  totalUnits?: number;
  unitValue?: number;
  segmentUnits?: SegmentUnits;
}): RateCardComparisonZone => {
  const { center, lowerBound, upperBound, rangeRadius } =
    calculateRateCardMetrics(referenceValue);

  const effectiveUnitValue =
    unitValue ?? (2 * rangeRadius) / segmentUnits.green;
  const minValue = center - (totalUnits / 2) * effectiveUnitValue;

  const leftRedEnd = minValue + segmentUnits.red * effectiveUnitValue;
  // greenEnd corresponds to upperBound in the new centered logic
  const rightOrangeEnd = upperBound + segmentUnits.orange * effectiveUnitValue;

  if (value >= lowerBound && value <= upperBound) {
    return "green";
  }

  // Fallback to approximate zones for visual bands outside strict green
  if (value <= leftRedEnd) return "redLow";
  if (value < lowerBound) return "orangeLow";
  if (value <= rightOrangeEnd) return "orangeHigh";
  return "redHigh";
};

/**
 * Display-only slider that positions a value relative to a reference value.
 * It renders red/orange/green bands and a top indicator showing the current value.
 */
export const RateCardComparisonSlider = ({
  referenceValue,
  value,
  currency,
  totalUnits = DEFAULT_TOTAL_UNITS,
  unitValue,
  segmentUnits = DEFAULT_SEGMENT_UNITS,
  className,
  variant,
}: Props) => {
  const { center, lowerBound, upperBound, rangeRadius } =
    calculateRateCardMetrics(referenceValue);

  const effectiveUnitValue =
    unitValue ?? (2 * rangeRadius) / segmentUnits.green;

  const minValue = center - (totalUnits / 2) * effectiveUnitValue;
  const maxValue = center + (totalUnits / 2) * effectiveUnitValue;
  const clampedValue = clamp(value, minValue, maxValue);
  const range = maxValue - minValue;
  const positionPercent =
    range === 0 ? 50 : ((clampedValue - minValue) / range) * 100;

  const greenUnits = Math.max(
    0,
    totalUnits - 2 * (segmentUnits.red + segmentUnits.orange),
  );

  // The outer regions are rendered as a single neutral area on each side.
  const sideUnits = segmentUnits.red + segmentUnits.orange;
  const leftNeutralWidth = (sideUnits / totalUnits) * 100;
  const greenWidth = (greenUnits / totalUnits) * 100;
  const rightNeutralWidth = leftNeutralWidth;

  const zone = getRateCardComparisonZone({
    referenceValue,
    value,
    totalUnits,
    unitValue,
    segmentUnits,
  });

  const getIndicatorColor = () => {
    // Brand variant
    if (variant === "brand") {
      if (zone === "redLow" || zone === "orangeLow" || zone === "green") {
        return "bg-green-600";
      }
      if (zone === "orangeHigh") {
        return "bg-orange-300";
      }
      return "bg-pink-600";
    }

    // Creator variant
    if (zone === "green") {
      return "bg-green-600";
    }
    if (zone === "orangeLow" || zone === "orangeHigh") {
      return "bg-orange-300";
    }
    return "bg-pink-600";
  };

  const indicatorBgClass = getIndicatorColor();

  const roundedGreenStartValue = lowerBound;
  const roundedGreenEndValue = upperBound;

  const arrowLeft = `clamp(${ARROW_EDGE_OVERHANG_PX}px, ${toPercentString(
    positionPercent,
  )}, calc(100% - ${ARROW_EDGE_OVERHANG_PX}px))`;

  const formatCurrency = (amount: number) =>
    toCurrency(Math.round(amount), currency, 0);

  return (
    <div className={cn("relative w-full pt-4 pb-1", className)}>
      {/* Top indicator (arrow): shows current value position */}
      <div
        className="absolute top-0 transition-[left] duration-200 ease-out motion-reduce:transition-none"
        style={{ left: arrowLeft }}
      >
        <div className="-translate-x-1/2">
          {/* Indicator pill: color changes by segment (pink/orange/green) */}
          <div
            className={cn(
              "flex h-3 w-3 items-center justify-center rounded-sm transition-colors duration-200 ease-out motion-reduce:transition-none",
              indicatorBgClass,
            )}
          >
            <ArrowDown className="h-2 w-2 stroke-2! text-white" />
          </div>
        </div>
      </div>

      {/* Slider bar: segmented background with a highlighted green band */}
      <div
        className="bg-primary-50 w-full rounded"
        role="img"
        aria-label={`Rate card comparison. Reference ${formatCurrency(referenceValue)}. Current ${formatCurrency(value)}.`}
      >
        {/* Segment container: neutral / green highlighted range / neutral */}
        <div className="flex h-3 w-full overflow-hidden rounded">
          {/* Left neutral region (combined red + orange) */}
          <div
            className="bg-primary-50 h-full"
            style={{ width: toPercentString(leftNeutralWidth) }}
          />

          {/* Middle green band: highlighted range with min/max labels */}
          <div
            className="h-full rounded"
            style={{ width: toPercentString(greenWidth) }}
          >
            <div className="relative flex h-full w-full items-center justify-between gap-1 rounded bg-green-600 px-1 outline-2 outline-offset-0 outline-white">
              {/* Green range min label */}
              <Typography
                size="sm"
                weight="semibold"
                className="leading-2.75 whitespace-nowrap text-white tabular-nums"
              >
                {formatCurrency(roundedGreenStartValue)}
              </Typography>

              {/* Green range max label */}
              <Typography
                size="sm"
                weight="semibold"
                className="leading-2.75 whitespace-nowrap text-white tabular-nums"
              >
                {formatCurrency(roundedGreenEndValue)}
              </Typography>
            </div>
          </div>

          {/* Right neutral region (combined orange + red) */}
          <div
            className="bg-primary-50 h-full"
            style={{ width: toPercentString(rightNeutralWidth) }}
          />
        </div>
      </div>
    </div>
  );
};
